<template>
  <slot
    :label="label"
    :disabled="disabled"
    :error="error"
    :default-input-binds="{
      label,
      disabled,
      hint: error,
      isError: !!error,
      emptyValueTo: 'null' as 'null',
      onBlur: () => onSubmitField(),
    }"
  />
</template>

<script setup lang="ts" generic="F extends keyof Booking">
import type { Booking } from '@/entities/bookings/booking.entity';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import { useUpdateBooking } from '@/queries/use-bookings';
import { cloneDeep } from 'lodash';
import { useForm } from 'vee-validate';
import { ref, watch } from 'vue';
import * as yup from 'yup';

const props = defineProps<{
  label?: string;
  disabled?: boolean;
  error?: string;
  booking: Booking;
  fieldName: F;
  validationSchema?: yup.AnySchema;
}>();

const { mutateAsync: updateBooking } = useUpdateBooking();

const {
  values: formValues,
  isFieldValid,
  resetField,
} = useForm({
  initialValues: {
    [props.fieldName]: props.booking[props.fieldName] as any,
  },
  validationSchema: {
    [props.fieldName]: props.validationSchema,
  },
});

const unchangedValues = ref(cloneDeep(formValues));
const hasChanged = useFormHasChanged(unchangedValues, formValues);

watch(
  () => props.booking[props.fieldName],
  () => {
    if (!hasChanged.value) {
      resetField(props.fieldName, {
        value: props.booking[props.fieldName],
      });
      unchangedValues.value[props.fieldName] = cloneDeep(formValues[props.fieldName]);
    }
  },
);

const onSubmitField = async () => {
  if (!(isFieldValid(props.fieldName) && hasChanged.value)) {
    return;
  }
  await updateBooking({
    id: props.booking.id,
    booking: {
      [props.fieldName]: formValues[props.fieldName],
    },
  });
  unchangedValues.value[props.fieldName] = cloneDeep(formValues[props.fieldName]);
};
</script>
